/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
import React,{useState,useEffect,useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import {useAuth} from '../hooks/useAuth';
import api from '../services/api';
import {fmtDateTime,safeArray} from '../utils/helpers';

const ORDER_TYPE_META:Record<string,{icon:string;label:string;color:string}> = {
  PHARMACY:  {icon:'💊', label:'Pharmacy',  color:'bg-green-100 text-green-700'},
  LAB:       {icon:'🔬', label:'Lab',       color:'bg-blue-100 text-[#02357A]'},
  RADIOLOGY: {icon:'🩻', label:'Radiology', color:'bg-purple-100 text-purple-700'},
  PROCEDURE: {icon:'🏥', label:'Procedure', color:'bg-amber-100 text-amber-700'},
  DIET:      {icon:'🍽️', label:'Diet',      color:'bg-orange-100 text-orange-700'},
  NURSING:   {icon:'👩‍⚕️', label:'Nursing',  color:'bg-pink-100 text-pink-700'},
};

export default function DoctorDashboardPage(){
  const {user}=useAuth();
  const navigate=useNavigate();
  const [dashboard,setDashboard]=useState<any>({});
  const [appointments,setAppointments]=useState<any[]>([]);
  const [referrals,setReferrals]=useState<any[]>([]);
  const [cpoeOrders,setCpoeOrders]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);

  const load=useCallback(async()=>{
    if(!user?.id)return;
    setLoading(true);
    try{
      const [d,a,r,c] = await Promise.all([
        api.getDoctorDashboard(user.id),
        api.getAppointments({doctorId:user.id,date:new Date().toISOString().split('T')[0]}).catch(()=>({data:[]})),
        api.getReferrals({status:'PENDING'}).catch(()=>({data:[]})),
        api.getCPOEOrders({doctorId:user.id,status:'ORDERED'}).catch(()=>({data:[]})),
      ]);
      setDashboard(d.data||{});
      setAppointments(Array.isArray(a.data?.appointments)?a.data.appointments:safeArray(a.data,['appointments']));
      setReferrals(Array.isArray(r.data)?r.data:safeArray(r.data));
      setCpoeOrders(Array.isArray(c.data)?c.data:safeArray(c.data));
    }catch{}finally{setLoading(false);}
  },[user]);

  useEffect(()=>{load();},[user]);

  const ordersByType:Record<string,number> = {};
  (dashboard.ordersByType||[]).forEach((o:any)=>{ordersByType[o.orderType||o.order_type]=+o.count;});

  return(
    <Layout title={`Welcome, Dr. ${user?.firstName||''} ${user?.lastName||''}`}>
      {/* Quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard label="Today's Appointments" value={dashboard.todayAppointments||0} icon="📅" color="blue"/>
        <StatCard label="Today's Consultations" value={dashboard.todayConsultations||0} icon="🩺" color="green"/>
        <StatCard label="Pending CPOE Orders"   value={dashboard.pendingOrders||0}      icon="📋" color="amber"/>
        <StatCard label="Pending Referrals"     value={dashboard.pendingReferrals||0}   icon="↗️" color="purple"/>
      </div>

      {/* Quick action */}
      <div className="mb-5">
        <button onClick={()=>navigate('/doctor/emr')}
          className="btn-primary text-base py-3 px-6 font-bold shadow-lg">
          🩺 Open EMR Workspace — Search Patient
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Today's appointments */}
        <div className="lg:col-span-2 card overflow-hidden">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-semibold text-slate-700">📅 Today's Appointments</h3>
            <button onClick={load} className="text-xs text-[#02357A] hover:underline">🔄 Refresh</button>
          </div>
          {loading?(
            <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"/></div>
          ):appointments.length===0?(
            <div className="p-10 text-center text-slate-400">
              <span className="text-3xl block mb-2">📅</span>No appointments today
            </div>
          ):(
            <div className="divide-y">
              {appointments.map((a:any,i:number)=>(
                <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-blue-100 text-[#02357A] flex items-center justify-center font-bold text-sm">
                      {(a.patient_first_name||a.patientName||'?')[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">
                        {a.patient_first_name ? `${a.patient_first_name} ${a.patient_last_name||''}`.trim() : (a.patientName||'Unknown Patient')}
                      </p>
                      <p className="text-xs text-slate-400">{a.consultation_type||a.appointment_type||a.appointmentType||'Consultation'} · Token #{a.token_number||a.token_number||a.tokenNumber||a.queue_number||'—'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500">
                      {a.slotTime||fmtDateTime(a.appointmentDate||a.appointment_date)}
                    </span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                      ${a.status==='COMPLETED'?'bg-green-100 text-green-700':
                        a.status==='IN_PROGRESS'?'bg-amber-100 text-amber-700':
                        'bg-blue-100 text-[#02357A]'}`}>{a.status}</span>
                    <button onClick={()=>{
                      const pid=a.patient_id||a.patientId;
                      const pname=`${a.patient_first_name||''} ${a.patient_last_name||''}`.trim();
                      const isOphthalmologist=user?.specialization==='Ophthalmology';
                      if(isOphthalmologist){
                        navigate('/ophthalmology',{state:{patientId:pid,patientName:pname}});
                      }else{
                        navigate('/doctor/emr',{state:{patientId:pid,patientName:pname}});
                      }
                    }}
                      className="btn-primary text-xs py-1 px-3">{user?.specialization==='Ophthalmology'?'Open Exam':'Open EMR'}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Side column */}
        <div className="space-y-4">
          {/* Pending CPOE by type */}
          <div className="card p-4">
            <h3 className="font-semibold text-slate-700 mb-3 text-sm">📋 Pending Orders by Type</h3>
            {Object.keys(ordersByType).length===0?(
              <p className="text-slate-400 text-sm text-center py-4">No pending orders</p>
            ):(
              <div className="space-y-2">
                {Object.entries(ordersByType).map(([type,count])=>{
                  const meta=ORDER_TYPE_META[type]||{icon:'📄',label:type,color:'bg-slate-100 text-slate-600'};
                  return(
                    <div key={type} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                      <span className="text-sm flex items-center gap-2"><span>{meta.icon}</span> {meta.label}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${meta.color}`}>{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pending referrals */}
          <div className="card p-4">
            <h3 className="font-semibold text-slate-700 mb-3 text-sm">↗️ Pending Referrals</h3>
            {referrals.length===0?(
              <p className="text-slate-400 text-sm text-center py-4">No pending referrals</p>
            ):(
              <div className="space-y-2">
                {referrals.slice(0,5).map((r:any,i:number)=>(
                  <div key={i} className="p-2 rounded-lg bg-slate-50 text-sm">
                    <p className="font-semibold text-slate-700">{r.referredToDepartment||r.referred_to_department}</p>
                    <p className="text-xs text-slate-400">{r.referralNumber||r.referral_number} · {r.urgency}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick links */}
          <div className="card p-4">
            <h3 className="font-semibold text-slate-700 mb-3 text-sm">⚡ Quick Links</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                ['🩺 EMR Workspace','/doctor/emr'],
                ['📅 Appointments','/appointments'],
                ['🏥 OPD','/opd'],
                ['🛏️ IPD','/ipd'],
              ].map(([l,p])=>(
                <button key={p} onClick={()=>navigate(p)} className="btn-secondary text-xs py-2">{l}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
